import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Phone, Check, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PREF_LABELS = [
  { key: "appointments", label: "Appointment reminders", hint: "24h and 1h before scheduled visits." },
  { key: "meds", label: "Medication time alerts", hint: "Daily SMS at medication times you've set." },
  { key: "tasks", label: "Task due dates", hint: "Morning ping for tasks due today from the Hope Planner." },
  { key: "care_activity", label: "Care-team activity", hint: "When a sibling or contributor logs something for one of your chests." },
];

export default function SmsSettingsCard({ user }) {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("idle"); // idle | entering_phone | awaiting_code
  const [phoneInput, setPhoneInput] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const r = await api.get("/me/sms-status");
      setStatus(r.data);
      if (r.data.phone_e164 && !r.data.phone_verified) setStep("awaiting_code");
      else setStep("idle");
    } catch (e) { /* noop */ }
  };

  const isPremium = user?.subscription_tier === "paid";

  const startVerification = async () => {
    if (!agreed) { toast.error("Please accept the SMS opt-in terms to continue."); return; }
    if (!phoneInput.trim()) { toast.error("Enter a phone number first."); return; }
    setBusy(true);
    try {
      const r = await api.post("/me/phone/start-verification", { phone: phoneInput });
      toast.success(r.data.message || "Code sent — check your phone.");
      setStep("awaiting_code");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code");
    } finally { setBusy(false); }
  };

  const checkCode = async () => {
    if (!code.trim()) { toast.error("Enter the 6-digit code from your text message."); return; }
    setBusy(true);
    try {
      await api.post("/me/phone/check-verification", { code });
      toast.success("Phone verified ✓ You can now turn on SMS reminders.");
      setCode("");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code did not match");
    } finally { setBusy(false); }
  };

  const removePhone = async () => {
    if (!window.confirm("Remove your phone number? SMS reminders will stop immediately.")) return;
    try {
      await api.post("/me/phone/remove");
      toast.success("Phone removed.");
      setPhoneInput("");
      setAgreed(false);
      await refresh();
    } catch (err) {
      toast.error("Could not remove phone");
    }
  };

  const toggleOptIn = async (next) => {
    try {
      await api.put("/me/sms-prefs", { sms_opt_in: next });
      toast.success(next ? "SMS reminders enabled" : "SMS reminders paused");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const togglePref = async (key, value) => {
    const next = { ...(status?.sms_prefs || {}), [key]: value };
    try {
      await api.put("/me/sms-prefs", { sms_prefs: next });
      await refresh();
    } catch (err) {
      toast.error("Could not save preference");
    }
  };

  if (!status) return null;

  const phoneOnFile = !!status.phone_e164;
  const verified = !!status.phone_verified;

  return (
    <div className="hc-card p-6 mt-6" data-testid="sms-settings-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#E8DCC8] flex items-center justify-center flex-shrink-0">
            <Phone className="w-5 h-5 text-[#3A5A4A]" />
          </div>
          <div>
            <h2 className="font-display text-xl flex items-center gap-2">
              SMS reminders
              {!isPremium && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E8DCC8] text-[#3A5A4A]">Premium</span>}
            </h2>
            <p className="text-sm text-[#7A736E] mt-1">
              Get a text on your phone for the things that matter most — appointments, medications, tasks.
            </p>
          </div>
        </div>
      </div>

      {!isPremium && (
        <div className="rounded-xl bg-[#F3EFEA] p-4 text-sm text-[#7A736E]" data-testid="sms-premium-gate">
          You can save your phone number on any plan. SMS reminders are a Premium feature — <a href="/billing" className="text-[#3A5A4A] underline">upgrade</a> to enable them.
        </div>
      )}

      {/* Phone entry / management */}
      {!phoneOnFile && (
        <div className="space-y-3" data-testid="sms-add-phone">
          <Label htmlFor="phone-input" className="text-sm">Your mobile number</Label>
          <Input
            id="phone-input"
            type="tel"
            placeholder="(555) 123-4567"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            className="rounded-xl border-[#EBE5DF]"
            data-testid="sms-phone-input"
          />
          <label className="flex items-start gap-2 text-xs text-[#7A736E] cursor-pointer" data-testid="sms-tcpa-label">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5"
              data-testid="sms-tcpa-checkbox"
            />
            <span>
              I agree to receive automated SMS reminders from Hope Chest at the number above. Message frequency varies. Msg &amp; data rates may apply. Reply <strong>STOP</strong> to unsubscribe, <strong>HELP</strong> for help. See our <a href="/privacy" className="underline">Privacy Policy</a> and <a href="/terms" className="underline">Terms</a>.
            </span>
          </label>
          <Button
            onClick={startVerification}
            disabled={busy || !agreed || !phoneInput.trim()}
            className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]"
            data-testid="sms-send-code"
          >
            {busy ? "Sending…" : "Send verification code"}
          </Button>
        </div>
      )}

      {phoneOnFile && !verified && step === "awaiting_code" && (
        <div className="space-y-3" data-testid="sms-verify-section">
          <p className="text-sm">
            We sent a 6-digit code to <strong>{status.phone_e164}</strong>. Enter it below.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="rounded-xl border-[#EBE5DF] w-40 font-mono text-lg tracking-widest"
              data-testid="sms-code-input"
            />
            <Button onClick={checkCode} disabled={busy || code.length !== 6} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="sms-verify-button">
              {busy ? "Checking…" : "Verify"}
            </Button>
            <Button onClick={removePhone} variant="outline" className="rounded-full border-[#EBE5DF]" data-testid="sms-cancel-verify">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {phoneOnFile && verified && (
        <div className="space-y-4" data-testid="sms-verified-section">
          <div className="flex items-center justify-between gap-4 p-3 rounded-xl bg-[#F3EFEA]">
            <div className="flex items-center gap-2 text-sm">
              <ShieldCheck className="w-4 h-4 text-[#3A5A4A]" />
              <strong className="text-[#3D3935]">{status.phone_e164}</strong>
              <span className="text-xs text-[#3A5A4A]">verified ✓</span>
            </div>
            <button onClick={removePhone} className="text-xs text-[#C87B67] hover:underline" data-testid="sms-remove">
              Remove
            </button>
          </div>

          <div className="flex items-center justify-between gap-4 py-3 border-t border-[#EBE5DF]">
            <div>
              <Label htmlFor="sms-master-toggle" className="text-sm font-medium cursor-pointer">SMS reminders {isPremium ? "" : "(Premium)"}</Label>
              <p className="text-xs text-[#7A736E] mt-1">Master switch for all SMS notifications.</p>
            </div>
            <Switch
              id="sms-master-toggle"
              checked={!!status.sms_opt_in}
              onCheckedChange={(c) => toggleOptIn(c)}
              disabled={!isPremium || !status.twilio_configured}
              data-testid="sms-master-toggle"
            />
          </div>

          {status.sms_opt_in && (
            <div className="space-y-1" data-testid="sms-prefs-section">
              <p className="text-xs uppercase tracking-wider text-[#7A736E] mt-2">What to text me about</p>
              {PREF_LABELS.map((p) => (
                <div key={p.key} className="flex items-center justify-between gap-4 py-2.5">
                  <div>
                    <Label htmlFor={`pref-${p.key}`} className="text-sm font-medium cursor-pointer">{p.label}</Label>
                    <p className="text-xs text-[#7A736E] mt-0.5">{p.hint}</p>
                  </div>
                  <Switch
                    id={`pref-${p.key}`}
                    checked={!!(status.sms_prefs && status.sms_prefs[p.key])}
                    onCheckedChange={(c) => togglePref(p.key, c)}
                    data-testid={`sms-pref-${p.key}`}
                  />
                </div>
              ))}
            </div>
          )}

          {!status.twilio_configured && (
            <p className="text-xs text-[#C87B67] mt-2" data-testid="sms-not-configured-note">
              <Check className="w-3 h-3 inline mr-1" /> Phone number saved. SMS sending will activate once Twilio is connected on the server.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
